import {
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  PATIENT_BENEFITS,
  PATIENT_NAVIGATION_BAR_PERMISSIONS,
  REFERRAL_PATIENT_BENEFITS,
} from "app/access-control/common-permission-asks";
import { hasAccess } from "app/access-control/permission-verification";
import {
  Pages,
  PatientChartBenefits,
  PatientProfile,
  ReferralPatientChartBenefits,
} from "app/access-control/permissions";
import { Events, NavParams, ToastController } from "ionic-angular";
import { Subscription } from "rxjs";
import { AccessControlService } from "../../../app/access-control/access-control-service";
import { AuthService } from "../../../app/auth/auth.service";
import { NavigationService } from "../../../navigation/navigation.service";
import {
  AppStatus,
  ConnectionStatusEnum,
} from "../../../providers/app-status/app-status";
import { commonVar } from "../../../providers/common-var/common";
import { ConfigurationService } from "../../../providers/configuration/configuration.service";
import { MobileUtilsProvider } from "../../../providers/mobile-utils/mobile-utils";
import { NavBarServiceProvider } from "../../../providers/nav-bar-service/nav-bar-service";
import { ReferralsServiceProvider } from "../../../providers/patient-charts/referrals-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";

interface NavItem {
  isOldPatientChart: boolean;
  label: string;
  icon: string;
  active: boolean;
  isPatientChart: boolean;
  useExternalId: boolean;
  iconName: string;
  hisFeatureToggled: boolean;
  url: string;
  css: string;
  id: string;
}

@Component({
  selector: "nav-bar",
  templateUrl: "nav-bar.html",
})
export class NavBarComponent implements OnInit, OnChanges, OnDestroy {
  private navTabs: NavItem[];
  isMobilePlatform: boolean;
  appSyncStatus: ConnectionStatusEnum;
  patientCareType: String = "";
  patientStatus: String = "";
  patientCareTypeSubscription: Subscription;
  @Output()
  extPatientId: number;

  constructor(
    private accessCntrl: AccessControlService,
    private referralServiceProvider: ReferralsServiceProvider,
    private navBarService: NavBarServiceProvider,
    private toastCntrl: ToastController,
    private events: Events,
    public mobileUtilsProvider: MobileUtilsProvider,
    public appStatus: AppStatus,
    private configurationService: ConfigurationService,
    public stateData: StateManagementService,
    public navParams: NavParams,
    private authService: AuthService,
    private navSvc: NavigationService
  ) {
    this.isMobilePlatform = mobileUtilsProvider.isMobilePlatform();
    this.appSyncStatus = this.appStatus.getAppStatus();
    this.events.unsubscribe("reloadNavBar");
    this.events.subscribe("reloadNavBar", () => {
      this.initializeNavBar();
    });
    this.navBarService.subscribeToReloadNavBar(
      this.initializeNavBar.bind(this)
    );
    this.patientCareType = this.stateData.patientCareType;
    this.patientStatus = this.stateData.patientChartStatus;
  }

  ngOnInit(): void {
    if (!this.navBarService.featureToggleData) {
      this.navBarService.initFeatureToggleData();
    }

    this.patientCareTypeSubscription = this.stateData
      .getPatientCareTypeObserver()
      .subscribe((careType: String) => {
        this.patientCareType = careType;
      });

    // NOVA-1595: self-bootstrap so the nav doesn't strand when the
    // reloadNavBar event fires before this component subscribes.
    this.initializeNavBar();
  }

  ngOnChanges(changes: SimpleChanges) {}

  initializeNavBar() {
    this.extPatientId = this.navBarService.getExternalId();
    this.appSyncStatus = this.appStatus.getAppStatus();
    this.createLeftNavigationData();
  }

  isCurrentTab(url: RegExp) {
    let href = window.location.href;
    return href.search(url) != -1;
  }

  getSideBarIcon(type: string, iconName: string) {
    if (type) {
      switch (type) {
        case "Profile":
          return this.getIcon(iconName);

        case "ePrescribe":
          return this.getIcon(iconName);

        case "Careteam":
          return this.getIcon(iconName);

        case "Careplan":
          return this.getIcon(iconName);

        case "Patient Calendar":
          return this.getIcon(iconName);

        case "Benefits":
          return this.getIcon(iconName);

        case "Certifications":
          return this.getIcon(iconName);

        case "Consents":
          return this.getIcon(iconName);

        case "Med. & DME":
          return this.getIcon(iconName);

        default:
          return "";
      }
    } else {
      return "";
    }
  }

  getIcon(iconName: string) {
    if (iconName === "md-add-circle") {
      return "md-add";
    } else if (iconName === "md-checkmark-circle") {
      return "md-checkmark";
    } else if (iconName === "md-radio-button-off") {
      return "md-radio-button-off";
    } else if (iconName === "ePrescibe") {
      return;
    } else {
      return "md-add";
    }
  }

  getMedicationAndDMESideBarIcon(patientDetailFlags: any) {
    if (
      patientDetailFlags &&
      (patientDetailFlags.allergiesPresent ||
        patientDetailFlags.dmePresent ||
        patientDetailFlags.medicationPresent)
    ) {
      return this.getSideBarIcon("Med. & DME", commonVar.COMPLETED_ICON);
    } else {
      return "md-add";
    }
  }

  populateNavigationData(sidebarData: any) {
    this.navTabs.push(sidebarData);
  }

  goTo(index) {
    let tabObject = this.navTabs[index];
    let url = tabObject.url;
    let isPatientChart = tabObject.isPatientChart;
    let patientChartId: string;
    if (tabObject.useExternalId) {
      patientChartId = `${this.navBarService.getExternalId()}`;
    } else {
      patientChartId = `${this.navBarService.getPatientChartId()}`;
    }

    if (tabObject.label == "HIS/HOPE") {
      let reportsUrl = "";
      if (this.navBarService.featureToggleOn("HIS")) {
        let hisOrHopeUrl = this.navBarService.featureToggleOn("HOPE")
          ? "/hqrp-report-list"
          : "/report-list";
        reportsUrl =
          window.location.href.split("#")[0] +
          "#/reports/" +
          this.navBarService.getExternalId() +
          hisOrHopeUrl;
        window.localStorage.setItem("previousPage", tabObject.label);
      } else if (!this.navBarService.featureToggleOn("HIS")) {
        reportsUrl = `${
          this.configurationService.getConfig().logoutUrl
        }/Clinical/PatientChart/Index/${this.navBarService.getExternalId()}?tab=report&tenantId=${
          this.authService.getUser().tenantId
        }`;
      }

      if (reportsUrl) {
        window.location.href = reportsUrl;
      } else {
        this.presentToast("Selection is not currently available.");
      }
      this.navTabs.forEach((item) => (item.active = false));
      tabObject.active = true;
      return;
    }

    if (tabObject.label === "Careteam") {
      window.localStorage.setItem("previousPage", tabObject.label);
      let statusPath = this.navBarService.convertStatusUrlSection(
        this.navBarService.getPatientChartStatus()
      );
      if (statusPath && statusPath === "discharge") {
        statusPath = "patient";
      }
      let careteamUrl =
        window.location.href.split("#")[0] +
        "#/" +
        statusPath +
        "/" +
        this.navBarService.getExternalId() +
        tabObject.url;
      if (careteamUrl) {
        window.location.href = careteamUrl;
      } else {
        this.presentToast("Selection is not currently available.");
      }
      this.navTabs.forEach((item) => (item.active = false));
      tabObject.active = true;
      return;
    }

    if (tabObject.label === "Patient Calendar") {
      this.navSvc.goToInternalPage("NewCalendarPage", {
        patChartId: this.extPatientId,
      });
      return;
    }

    if (tabObject.label == "Patient Reports") {
      window.location.href = `${window.location.href.split("#")[0]}#/patient/${
        this.extPatientId
      }/patient-reports`;
      return;
    }

    if (tabObject.label == "Certifications") {
      let certificationUrl = "";
      if (this.navBarService.featureToggleOn("CTI_v2")) {
        certificationUrl =
          window.location.href.split("#")[0] +
          "#/patient/" +
          this.navBarService.getExternalId() +
          "/cti";
        window.localStorage.setItem("previousPage", tabObject.label);
      } else if (this.navBarService.featureToggleOn("CTI")) {
        certificationUrl =
          window.location.href.split("#")[0] +
          "#/patient/" +
          this.navBarService.getExternalId() +
          "/certs-consents";
        window.localStorage.setItem("previousPage", tabObject.label);
      } else if (!this.navBarService.featureToggleOn("CTI")) {
        if (
          this.navBarService.getPatientChartStatus() &&
          this.navBarService.getPatientChartStatus().toLowerCase() == "referral"
        ) {
          certificationUrl = `${
            this.configurationService.getConfig().logoutUrl
          }/Clinical/Patient/CreatePatient/${this.navBarService.getExternalId()}?tenantId=${
            this.authService.getUser().tenantId
          }`;
        } else {
          certificationUrl = `${
            this.configurationService.getConfig().logoutUrl
          }/Clinical/PatientChart/Index/${this.navBarService.getExternalId()}?tab=certification&tenantId=${
            this.authService.getUser().tenantId
          }`;
        }
      }

      if (certificationUrl) {
        window.location.href = certificationUrl;
      } else {
        this.presentToast("Selection is not currently available.");
      }
      this.navTabs.forEach((item) => (item.active = false));
      tabObject.active = true;
      return;
    }
    if (tabObject.label == "Careplan") {
      window.localStorage.setItem("previousPage", tabObject.label);
    }
    if (tabObject.label == "Documents") {
      let statusPath = this.navBarService.convertStatusUrlSection(
        this.navBarService.getPatientChartStatus()
      );
      if (statusPath && statusPath === "discharge") {
        statusPath = "patient";
      }
      window.localStorage.setItem("previousPage", tabObject.label);
    }

    if (!this.navBarService.featureToggleOn("Patient Chart")) {
      let goToUrl = this.buildUrlForNoPatientChartFeatureToggle(tabObject);
      if (goToUrl) {
        window.localStorage.setItem("previousPage", tabObject.label);
        window.location.href = goToUrl;
      } else {
        this.presentToast("Selection is not currently available.");
      }
      this.navTabs.forEach((item) => (item.active = false));
      tabObject.active = true;
      return;
    }

    let baseUrl =
      window.location.href.split("#")[0] + "#/patient/" + patientChartId + url;

    if (isPatientChart) {
      if (url == "report-list") {
        baseUrl =
          window.location.href.split("#")[0] +
          "#/reports/" +
          this.navBarService.getExternalId() +
          "/" +
          url;
      } else if (url === "certification/") {
        baseUrl =
          window.location.href.split("#")[0] +
          "#/patient/" +
          this.navBarService.getExternalId() +
          "/certs-consents";
      } else if (url === "pdb") {
        baseUrl =
          window.location.href.split("#")[0] +
          "#/patient/" +
          this.navBarService.getExternalId() +
          "/" +
          url;
      } else {
        baseUrl =
          window.location.href.split("#")[0] +
          "#/referral-tabs/" +
          this.navBarService.convertStatusUrlSection(
            this.navBarService.getPatientChartStatus()
          ) +
          "/" +
          url +
          patientChartId;
        window.localStorage.setItem("previousPage", tabObject.label);
      }

      this.navTabs.forEach((item) => (item.active = false));
      tabObject.active = true;
    }

    let isOldPatientChart = tabObject.isOldPatientChart;

    if (isOldPatientChart) {
      let patientStatus = this.navBarService.getPatientChartStatus();
      if (
        url == "patient-benefits/" &&
        patientStatus &&
        patientStatus.toLowerCase() !== "referral"
      ) {
        baseUrl =
          this.configurationService.getConfig().logoutUrl +
          "/Clinical/PatientChart/Index/" +
          this.navBarService.getExternalId() +
          "?tab=benefits&tenantId=" +
          this.authService.getUser().tenantId;
      } else if (
        url == "patient-benefits/" &&
        patientStatus &&
        patientStatus.toLowerCase() == "referral"
      ) {
        baseUrl =
          this.configurationService.getConfig().logoutUrl +
          "/Clinical/Patient/CreatePatient/" +
          this.navBarService.getExternalId() +
          "&tenantId=" +
          this.authService.getUser().tenantId;
      } else if (
        url == "claims" &&
        patientStatus &&
        patientStatus.toLowerCase() !== "referral"
      ) {
        baseUrl =
          this.configurationService.getConfig().logoutUrl +
          "/Clinical/PatientChart/Index/" +
          this.navBarService.getExternalId() +
          "?tab=claims&tenantId=" +
          this.authService.getUser().tenantId;
      } else if (
        url == "claims" &&
        patientStatus &&
        patientStatus.toLowerCase() == "referral"
      ) {
        baseUrl = "";
      } else if (url == "medication-dme/") {
        baseUrl =
          this.configurationService.getConfig().logoutUrl +
          "/Clinical/PatientChart/Index/" +
          this.navBarService.getExternalId() +
          "?tab=medication&tenantId=" +
          this.authService.getUser().tenantId;
      } else if (
        url == "medication-dme/" &&
        patientStatus &&
        patientStatus.toLowerCase() == "referral"
      ) {
        baseUrl = ""; //Do nothing
      } else if (
        url == "report-list" &&
        patientStatus &&
        patientStatus.toLowerCase() !== "referral"
      ) {
        baseUrl =
          this.configurationService.getConfig().logoutUrl +
          "/Clinical/PatientChart/Index/" +
          this.navBarService.getExternalId() +
          "?tab=report&tenantId=" +
          this.authService.getUser().tenantId;
      } else if (
        url == "report-list" &&
        patientStatus &&
        patientStatus.toLowerCase() == "referral"
      ) {
        baseUrl = "";
      } else {
        baseUrl =
          this.configurationService.getConfig().logoutUrl +
          "/Clinical/PatientChart/Index/" +
          this.navBarService.getExternalId() +
          "?tenantId=" +
          this.authService.getUser().tenantId;
      }
    }

    if (
      !this.navBarService.patientChartFeatureToggleOn() &&
      !isOldPatientChart &&
      tabObject.label !== "HIS"
    ) {
      baseUrl = this.buildUrlForLegacy(tabObject);
    }

    if (
      this.navBarService.patientChartFeatureToggleOn() &&
      this.navBarService.benefitsFeatureToggleOn() &&
      tabObject.label == "Benefits"
    ) {
      //TODO: update the url
      baseUrl =
        window.location.href.split("#")[0] +
        "#/referral-tabs/" +
        this.navBarService.convertStatusUrlSection(
          this.navBarService.getPatientChartStatus()
        ) +
        "/" +
        tabObject.url +
        this.navBarService.getPatientChartId();

      this.navTabs.forEach((item) => (item.active = false));
      tabObject.active = true;
    }

    if (baseUrl) {
      window.location.href = baseUrl;
    } else {
      this.presentToast("Selection is not currently available.");
    }
  }

  buildUrlForNoPatientChartFeatureToggle(tabObject): string {
    let url;
    switch (tabObject.label) {
      case "Profile":
      case "Consents":
        url = this.buildUrlForNoPatientChart("", false);
        break;
      case "Activity":
      case "Notes":
        url = this.buildUrlForNoPatientChart("", true);
        break;
      case "Meds/DME":
        url = this.buildUrlForNoPatientChart("?tab=medication", true);
        break;
      case "HIS":
        url = this.buildUrlForNoPatientChart("?tab=report", true);
        break;
      case "Claims":
        url = this.buildUrlForNoPatientChart("?tab=claims", true);
        break;
      case "Documents":
        url =
          window.location.href.split("#")[0] +
          "#/patient" +
          "/" +
          this.navBarService.getExternalId() +
          tabObject.url;
        break;
      case "Certifications":
        url = this.buildUrlForNoPatientChart("?tab=certification`", false);
        break;
      case "Benefits":
        url = this.buildUrlForNoPatientChart("?tab=benefits", false);
        break;
      case "Careteam":
        let statusPath = this.navBarService.convertStatusUrlSection(
          this.navBarService.getPatientChartStatus()
        );
        if (statusPath && statusPath === "discharge") {
          statusPath = "patient";
        }
        url =
          window.location.href.split("#")[0] +
          "#/" +
          statusPath +
          "/" +
          this.navBarService.getExternalId() +
          tabObject.url;
        break;
      case "Patient Calendar":
        let patientId = this.navBarService.getExternalId();
        this.navBarService.setChartId(patientId);
        url =
          window.location.href.split("#")[0] +
          "#/calendar/" +
          this.navBarService.getPatientChartId();
        break;
      case "Careplan":
        url =
          window.location.href.split("#")[0] +
          "#/patient" +
          "/" +
          this.navBarService.getExternalId() +
          tabObject.url;
        break;
      default:
        url = `${
          this.configurationService.getConfig().logoutUrl
        }/Clinical/PatientChart/Index/${this.navBarService.getExternalId()}?tenantId=${
          this.authService.getUser().tenantId
        }`;
    }
    return url;
  }

  buildUrlForLegacy(tabObject): string {
    let url;
    switch (tabObject.label) {
      case "Profile":
        url = this.UrlForLegacy("?tab=Profile", false);
        break;
      case "Activity":
        url = this.UrlForLegacy("?tab=activity", true);
        break;
      case "Notes":
        url = url = this.UrlForLegacy("?tab=notes", true);
        break;
      case "Certifications":
        url = this.UrlForLegacy("?tab=certification", false);
        break;
      case "Consents":
        url = this.UrlForLegacy("", false);
        break;
      case "Careteam":
        url =
          window.location.href.split("#")[0] +
          "#/" +
          this.navBarService.convertStatusUrlSection(
            this.navBarService.getPatientChartStatus()
          ) +
          "/" +
          this.navBarService.getExternalId() +
          tabObject.url;
        break;
      case "Careplan":
        url =
          window.location.href.split("#")[0] +
          "#/patient" +
          "/" +
          this.navBarService.getExternalId() +
          tabObject.url;
        break;
      default:
        url = `${
          this.configurationService.getConfig().logoutUrl
        }/Clinical/PatientChart/Index/${this.navBarService.getExternalId()}?tenantId=${
          this.authService.getUser().tenantId
        }`;
    }
    return url;
  }

  presentToast(message) {
    let toast = this.toastCntrl.create({
      message: message,
      duration: 3000,
      position: "below",
      cssClass: "error",
    });

    toast.onDidDismiss(() => {});

    toast.present();
  }

  buildUrlForNoPatientChart(tab: string, empty: boolean): string {
    if (!this.navBarService.getPatientChartStatus()) {
      return `${
        this.configurationService.getConfig().logoutUrl
      }/Clinical/PatientChart/Index/${this.navBarService.getExternalId()}${tab}?tenantId=${
        this.authService.getUser().tenantId
      }`;
    }

    if (
      !empty &&
      (this.navBarService.getPatientChartStatus().toLowerCase() == "referral" ||
        this.navBarService.getPatientChartStatus().toLowerCase() ==
          "evaluation")
    ) {
      return `${
        this.configurationService.getConfig().logoutUrl
      }/Clinical/Patient/CreatePatient/${this.navBarService.getExternalId()}?tenantId=${
        this.authService.getUser().tenantId
      }`;
    }

    return "";
  }

  UrlForLegacy(tab: string, empty: boolean): string {
    if (!this.navBarService.getPatientChartStatus()) {
      return `${
        this.configurationService.getConfig().logoutUrl
      }/Clinical/PatientChart/Index/${this.navBarService.getExternalId()}${tab}?tenantId=${
        this.authService.getUser().tenantId
      }`;
    }

    if (
      !empty &&
      this.navBarService.getPatientChartStatus().toLowerCase() == "referral"
    ) {
      return `${
        this.configurationService.getConfig().logoutUrl
      }/Clinical/Patient/CreatePatient/${this.navBarService.getExternalId()}?tenantId=${
        this.authService.getUser().tenantId
      }`;
    }

    return "";
  }

  ngOnDestroy() {
    if (this.patientCareTypeSubscription) {
      this.patientCareTypeSubscription.unsubscribe();
    }
  }

  // users branch
  addPatientActivity() {
    this.populateNavigationData({
      id: "activity",
      label: "Activity",
      icon: "activity",
      isOldPatientChart: false,
      active: this.isCurrentTab(/patient-activity/),
      url: "patient-activity/",
      isPatientChart: true,
      css: "",
      iconName: "",
    });
  }

  // users branch
  addPatientCarePlan() {
    this.populateNavigationData({
      id: "care-plan",
      label: "Careplan",
      icon: "carePlan",
      isOldPatientChart: false,
      url: "/carePlan/visits",
      css: "",
      useExternalId: true,
      active: this.isCurrentTab(/carePlan\/visits/),
      iconName: "",
    });
  }

  // users branch
  addPatientCareTeam() {
    this.populateNavigationData({
      id: "care-team",
      label: "Careteam",
      icon: "careTeam",
      url: "/careTeams",
      isOldPatientChart: false,
      css: "",
      useExternalId: true,
      active: this.isCurrentTab(/\/\d+\/careTeams/),
      iconName:
        this.navBarService.getPatientChartStatus() &&
        this.navBarService.getPatientChartStatus().toLowerCase() == "referral"
          ? this.getSideBarIcon(
              "Careteam",
              this.referralServiceProvider.CARE_TEAM_SIDEBAR_ICON
            )
          : "",
    });
  }

  // users branch
  addPatientProfile() {
    this.populateNavigationData({
      id: "profile",
      label: "Profile",
      icon: "profile",
      isOldPatientChart: false,
      active: this.isCurrentTab(/patient-details\/\d+/),
      url: "patient-details/",
      isPatientChart: true,
      css: "",
      iconName:
        this.navBarService.getPatientChartStatus() &&
        this.navBarService.getPatientChartStatus().toLowerCase() == "referral"
          ? this.getSideBarIcon(
              "Profile",
              this.referralServiceProvider.PATIENT_PROFILE_SIDEBAR_ICON
            )
          : "",
    });
  }

  // users branch
  addPatientSchedule() {
    this.populateNavigationData({
      id: "patient-calendar",
      label: "Patient Calendar",
      icon: "Calendar Icon big White",
      url: "PatientCalendar",
      //isOldPatientChart: false,
      css: "",
      useExternalId: true,
      active: this.isCurrentTab(/\/\d+\/new-scheduler/),
      iconName: this.getSideBarIcon(
        "PatientCalendar",
        this.referralServiceProvider.PATIENT_CALENDAR_ICON
      ),
    });
  }

  // users branch
  addPatientBenefit() {
    this.populateNavigationData({
      id: "benefits",
      label: "Benefits",
      icon: "benefits",
      css: "",
      url: "patient-benefits/",
      isOldPatientChart: true,
      active: this.isCurrentTab(/patient-benefits/) ? "active" : "",
      iconName:
        this.navBarService.getPatientChartStatus() &&
        this.navBarService.getPatientChartStatus().toLowerCase() == "referral"
          ? this.getSideBarIcon(
              "Benefits",
              this.referralServiceProvider.BENEFITS_SIDEBAR_ICON
            )
          : "",
    });
  }

  // users branch
  addPatientCertification() {
    this.populateNavigationData({
      id: "certifications",
      label: "Certifications",
      icon: "consents",
      url: "certification/",
      active:
        this.isCurrentTab(/\/\d+\/certs-consents/) ||
        this.isCurrentTab(/\/\d+\/cti/),
      isPatientChart: true,
      isOldPatientChart: false,
      css: "",
      useExternalId: true,
      iconName:
        this.navBarService.getPatientChartStatus() &&
        this.navBarService.getPatientChartStatus().toLowerCase() == "referral"
          ? this.getSideBarIcon(
              "Certifications",
              this.referralServiceProvider.CERTIFICATE_ICON
            )
          : "",
    });
  }

  // users branch
  addPatientAllergy() {
    this.populateNavigationData({
      id: "allergies",
      label: "Allergies",
      icon: "allergy",
      css: "",
      url: "patient-allergies/",
      isOldPatientChart: false,
      active: this.isCurrentTab(/patient-allergies/) ? "active" : "",
      isPatientChart: true,
      iconName: "",
    });
  }

  // users branch
  addPatientMedication() {
    this.navBarService.getScriptSurePatient().subscribe((scriptPatient) => {
      const ePrescribeAvailable =
        this.navBarService.patientEPrescribeFeatureToggleOn() &&
        scriptPatient?.organization;

      if (this.navBarService.patientMedicationsFeatureToggleOn()) {
        if (ePrescribeAvailable) {
          this.populateNavigationData({
            id: "meds",
            label: "eRx Meds",
            icon: "nav-bar-erx-meds",
            css: "",
            url: "patient-eprescribe/",
            isOldPatientChart: false,
            active: this.isCurrentTab(/patient-eprescribe/) ? "active" : "",
            isPatientChart: true,
            iconName: "",
          });
        }
      }
    });

    // Always show Intake Meds tab - removed feature toggle condition
    this.populateNavigationData({
      id: "meds",
      label: "Intake Meds",
      icon: "Meds",
      css: "",
      url: "patient-medications/",
      isOldPatientChart: false,
      active: this.isCurrentTab(/patient-medications/) ? "active" : "",
      isPatientChart: true,
      iconName: "",
    });
    if (
      this.patientCareType &&
      this.patientCareType.toLowerCase() !== "evaluation" &&
      !this.navBarService.patientMedicationsFeatureToggleOn()
    ) {
      this.populateNavigationData({
        id: "meds-dme",
        label: "Meds/DME",
        icon: "medsDme",
        css: "",
        isOldPatientChart: true,
        url: "medication-dme/",
        isPatientChart: true,
        useExternalId: true,
        iconName: "",
      });
    }
  }
  addMedDME() {
    if (
      this.patientCareType &&
      this.patientCareType.toLowerCase() !== "evaluation"
    ) {
      this.populateNavigationData({
        id: "meds-dme",
        label: "Meds/DME",
        icon: "medsDme",
        css: "",
        isOldPatientChart: true,
        url: "medication-dme/",
        isPatientChart: true,
        useExternalId: true,
        iconName: "",
      });
    }
  }

  // users branch
  addPatientConsent() {
    this.populateNavigationData({
      id: "consents",
      label: "Consents",
      icon: "Consents_icon",
      url: "consents-details/",
      active: this.isCurrentTab(/consents-details\/\d+/),
      isOldPatientChart: false,
      isPatientChart: true,
      css: "",
      useExternalId: false,
      iconName:
        this.navBarService.getPatientChartStatus() &&
        this.navBarService.getPatientChartStatus().toLowerCase() == "referral"
          ? this.getSideBarIcon(
              "Consents",
              this.referralServiceProvider.CONSENTS_ICON
            )
          : "",
    });
  }

  // users branch
  addPatientClaim() {
    this.populateNavigationData({
      id: "claims",
      label: "Claims",
      icon: "claims",
      css: "",
      url: "claims",
      useExternalId: true,
      isOldPatientChart: true,
      iconName: "",
    });
  }

  // users branch
  addPatientHIS() {
    this.populateNavigationData({
      id: "his",
      label: "HIS/HOPE",
      icon: "hishopeicon",
      css: "",
      isOldPatientChart: true,
      url: "report-list",
      active: this.isCurrentTab(/report-list/),
      isPatientChart: true,
      hisFeatureToggled: true,
      iconName: "",
    });
  }

  // users branch
  addNote() {
    this.populateNavigationData({
      id: "notes",
      label: "Notes",
      icon: "notes",
      css: "",
      isPatientChart: true,
      isOldPatientChart: false,
      url: "notes/",
      active: this.isCurrentTab(/notes/),
      iconName: "",
    });
  }

  // users branch
  addDocument() {
    this.populateNavigationData({
      id: "documents",
      label: "Documents",
      icon: "DocsManagement",
      url: "/docManagement/docManagement",
      isOldPatientChart: false,
      css: "",
      useExternalId: true,
      active: this.isCurrentTab(/\/\d+\/docManagement\/docManagement/),
      iconName: "",
    });
  }

  addBereavement() {
    if (
      this.navBarService.hasBereavementAccess() &&
      this.patientCareType &&
      this.patientCareType.toLowerCase() !== "evaluation"
    ) {
      this.populateNavigationData({
        id: "bereavement",
        label: "Bereavement",
        icon: "bereavement",
        url: "pdb",
        isPatientChart: true,
        isOldPatientChart: false,
        useExternalId: true,
        css: "",
        active: this.isCurrentTab(/pdb/),
        iconName: "",
      });
    }
  }

  // users branch
  createLeftNavigationData(retryCount: number = 0) {
    const permissions = [...PATIENT_NAVIGATION_BAR_PERMISSIONS, Pages.Visit];

    if (
      this.patientStatus.toLowerCase() === "evaluation" ||
      this.patientStatus.toLowerCase() === "referral"
    ) {
      permissions.push(...REFERRAL_PATIENT_BENEFITS);
    } else {
      permissions.push(...PATIENT_BENEFITS);
    }
    this.accessCntrl.getPermission(permissions).subscribe(
      (x) => {
        this.navTabs = [];
        // showing Profile
        if (hasAccess(PatientProfile.View, x.permissions)) {
          this.addPatientProfile();
        }
        // Show Activity Patients:PatientChart:Activity:VIEW ****
        if (hasAccess(PatientProfile.ActivityView, x.permissions)) {
          if (
            this.navBarService.getPatientChartStatus() &&
            (this.navBarService.getPatientChartStatus().toLowerCase() ===
              "discharged" ||
              this.navBarService.getPatientChartStatus().toLowerCase() ===
                "on-service")
          ) {
            this.addPatientActivity();
          }
        }

        // Show CarePlan
        if (hasAccess(Pages.Visit, x.permissions)) {
          this.addPatientCarePlan();
        }
        // Show CareTeam
        if (
          this.patientCareType &&
          this.patientCareType.toLowerCase() !== "evaluation" &&
          hasAccess(PatientProfile.CareTeamView, x.permissions)
        ) {
          this.addPatientCareTeam();
        }
        // Show Patient Calendar
        if (this.navBarService.featureToggleOn("Patient Calendar")) {
          this.populateNavigationData({
            id: "patient-calendar",
            label: "Patient Calendar",
            icon: "Calendar Icon big White",
            url: "PatientCalendar",
            //isOldPatientChart: false,
            css: "",
            useExternalId: true,
            active: this.isCurrentTab(/\/\d+\/new-scheduler/),
            iconName: this.getSideBarIcon(
              "PatientCalendar",
              this.referralServiceProvider.PATIENT_CALENDAR_ICON
            ),
          });
        }

        // Show Benefits
        if (
          this.patientStatus.toLowerCase() === "evaluation" ||
          this.patientStatus.toLowerCase() === "referral"
        ) {
          if (
            hasAccess(
              ReferralPatientChartBenefits.ReferralPatientChartBenefitsView,
              x.permissions
            )
          ) {
            this.addPatientBenefit();
          }
        } else {
          if (
            hasAccess(
              PatientChartBenefits.PatientChartBenefitsView,
              x.permissions
            )
          ) {
            this.addPatientBenefit();
          }
        }
        // Show Patient Reports Patients:PatientChart:Profile:Reports:VIEW ****
        // if (hasAccess(PatientProfile.ReportsView, x.permissions)) {
        this.populateNavigationData({
          id: "patient-reports",
          label: "Patient Reports",
          icon: "patientReports",
          url: "patient-reports/",
          active: this.isCurrentTab(/\/\d+\/patient-reports/),
          iconName: "",
          useExternalId: true,
          isPatientChart: true,
          isOldPatientChart: false,
        });
        // }

        // Show Certifications
        if (
          !this.navBarService.hasEligibilityAccess() &&
          this.navBarService.getPatientChartStatus() &&
          this.patientCareType &&
          this.patientCareType.toLowerCase() == "hospice"
        ) {
          this.addPatientCertification();
        }

        // Show Allergies
        if (hasAccess(PatientProfile.AllergyView, x.permissions)) {
          if (this.navBarService.patientAllergiesFeatureToggleOn()) {
            this.addPatientAllergy();
          }
        }
        // Show Meds - always show Intake Meds tab
        if (hasAccess(PatientProfile.MedicationView, x.permissions)) {
          this.addPatientMedication();
        }
        // Shows Eligibility *****
        if (
          this.navBarService.eligibilityFeatureToggleOn() &&
          this.patientCareType &&
          this.patientCareType.toLowerCase() !== "evaluation"
        ) {
          this.populateNavigationData({
            id: "eligibility",
            label: "Eligibility",
            icon: "eligibility",
            css: "",
            url: "eligibility/",
            isOldPatientChart: false,
            active: this.isCurrentTab(/eligibility/) ? "active" : "",
            isPatientChart: true,
            iconName: "",
          });
        }

        // Shows Consents
        if (hasAccess(PatientProfile.ConsentView, x.permissions)) {
          if (
            this.patientCareType &&
            this.patientCareType.toLowerCase() !== "evaluation"
          ) {
            this.addPatientConsent();
          }
        }

        // Shows Claims
        //MAV-1739: No longer used

        // Shows HIS Patients:PatientChart:HIS:VIEW
        if (hasAccess(PatientProfile.HISView, x.permissions)) {
          if (
            !(this.patientCareType === "Palliative") &&
            this.navBarService.getPatientChartStatus() &&
            (this.navBarService.getPatientChartStatus().toLowerCase() ===
              "discharged" ||
              this.navBarService.getPatientChartStatus().toLowerCase() ===
                "on-service") &&
            this.patientCareType &&
            this.patientCareType.toLowerCase() !== "evaluation"
          ) {
            this.addPatientHIS();
          }
        }

        // Shows Note View Patient:PatientChart:Profile:Note:VIEW
        if (hasAccess(PatientProfile.NoteView, x.permissions)) {
          this.addNote();
        }

        // Shows Document
        if (hasAccess(PatientProfile.DocumentView, x.permissions)) {
          this.addDocument();
        }

        // Show Bereavement
        if (hasAccess(PatientProfile.BereavementView, x.permissions)) {
          this.addBereavement();
        }
      },
      (err) => {
        // NOVA-1595: getPermission can error when IAM/auth isn't ready on
        // a fresh SPA load. Silent failure leaves navTabs undefined and
        // the nav blank until the user refreshes. Retry once after a beat.
        console.error(
          "[nav-bar] createLeftNavigationData getPermission failed",
          err
        );
        if (retryCount < 1) {
          setTimeout(() => this.createLeftNavigationData(retryCount + 1), 500);
        }
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/common/nav-bar/nav-bar.ts